import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { DatasetService } from "../../../services/DatasetService";
import DatasetSampling from "./Steps/DatasetSampling";
import DatasetName from "./Steps/DatasetName";
import DatasetReview from "./Steps/DatasetReview";
import DatasetBaseType from "./Steps/DatasetBaseType";
import { DatasetData } from "../../../model_gen/dataset";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { 
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  FileTextIcon
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { notification } from "antd";

export const NEW_DATASET_STORAGE_KEY = "datasetData";

interface StepItem {
  key: string;
  title: string;
  component: React.ReactNode;
}

const DatasetNew: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [s3PrincipalArn, setS3PrincipalArn] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });

  const [datasetData, setDatasetData] = useState<DatasetData>(() => {
    const saved = localStorage.getItem(NEW_DATASET_STORAGE_KEY);
    
    // Get URL parameters for prepopulation
    const baseType = searchParams.get("base_type") as "intent_group" | "application_intent" | undefined;
    const intentGroupId = searchParams.get("intent_group_id");
    const applicationId = searchParams.get("application_id");
    const intentId = searchParams.get("intent_id");
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          base_type: baseType || parsed.base_type,
          intent_group_id: intentGroupId ? parseInt(intentGroupId) : parsed.intent_group_id,
          application_id: applicationId ? parseInt(applicationId) : parsed.application_id,
          intent_id: intentId ? parseInt(intentId) : parsed.intent_id,
          sample_type: parsed.sample_type || "all",
          sample_percentage: parsed.sample_percentage || 100,
        };
      } catch (e) {
        console.error("Error parsing saved dataset data", e);
        return {
          base_type: baseType,
          intent_group_id: intentGroupId ? parseInt(intentGroupId) : undefined,
          application_id: applicationId ? parseInt(applicationId) : undefined,
          intent_id: intentId ? parseInt(intentId) : undefined,
          sample_type: "all",
          sample_percentage: 100,
        };
      }
    }
    
    return {
      base_type: baseType,
      intent_group_id: intentGroupId ? parseInt(intentGroupId) : undefined,
      application_id: applicationId ? parseInt(applicationId) : undefined,
      intent_id: intentId ? parseInt(intentId) : undefined,
      sample_type: "all",
      sample_percentage: 100,
    };
  });

  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(NEW_DATASET_STORAGE_KEY, JSON.stringify(datasetData));
  }, [datasetData]);

  // Update URL when step changes
  useEffect(() => {
    // Only update URL if step parameter should be present (when step > 0 or step was already in URL initially)
    const currentStepParam = searchParams.get("step");
    const shouldHaveStepParam = currentStep > 0 || currentStepParam !== null;
    
    if (shouldHaveStepParam && currentStepParam !== currentStep.toString()) {
      const currentParams = Object.fromEntries(searchParams.entries());
      setSearchParams({
        ...currentParams,
        step: currentStep.toString()
      }, { replace: true }); // Use replace to avoid creating new history entries for step changes
    }
  }, [currentStep, searchParams, setSearchParams]);

  // Fetch S3 sync principal ARN once when component mounts
  useEffect(() => {
    if (!apiKey || s3PrincipalArn) return;

    const subscription = DatasetService.getS3SyncerInfo(undefined, apiKey).subscribe(
      (resp: any) => {
        const principalArn: string | undefined = resp?.sync_principal_arn;
        if (principalArn) setS3PrincipalArn(principalArn);
      },
      (error: any) => {
        console.error("Failed to fetch S3 syncer info:", error);
      }
    );

    return () => subscription.unsubscribe();
  }, [apiKey, s3PrincipalArn]);

  const steps: StepItem[] = [
    {
      key: "base-type",
      title: "Base Type",
      component: (
        <DatasetBaseType
          baseType={datasetData.base_type}
          intentGroupId={datasetData.intent_group_id}
          applicationId={datasetData.application_id}
          intentId={datasetData.intent_id}
          ignoredApplicationIds={datasetData.ignored_application_ids}
          onChange={(data: Partial<DatasetData>) =>
            setDatasetData((prev) => ({ ...prev, ...data }))
          }
        />
      ),
    },
    {
      key: "data-source",
      title: "Data Source",
      component: (
        <DatasetSampling
          datasetData={datasetData}
          onChange={(data: Partial<DatasetData>) =>
            setDatasetData((prev) => ({ ...prev, ...data }))
          }
          ignoredApplicationIds={datasetData.ignored_application_ids}
          s3PrincipalArn={s3PrincipalArn}
        />
      ),
    },
    {
      key: "details",
      title: "Details",
      component: (
        <DatasetName
          name={datasetData.name}
          description={datasetData.description}
          onChange={(name: string, description: string) =>
            setDatasetData((prev) => ({ ...prev, name, description }))
          }
        />
      ),
    },
    {
      key: "review",
      title: "Review",
      component: <DatasetReview datasetData={datasetData} apiKey={apiKey} />,
    },
  ];

  const canProceedFromStep = (stepIndex: number) => {
    const meta: any = datasetData.meta || {};
    const datasourceType =
      meta.datasource_type || meta.data_source_type || "production";

    switch (stepIndex) {
      case 0:
        if (datasetData.base_type === "intent_group") {
          return datasetData.intent_group_id !== undefined;
        } else if (datasetData.base_type === "application_intent") {
          return (
            datasetData.application_id !== undefined &&
            datasetData.intent_id !== undefined
          );
        }
        return false;
      case 1:
        // Data source gating (manual + S3)
        if (datasourceType === "manual") {
          return Boolean(meta.uploaded_file);
        }
        if (datasourceType === "s3") {
          return Boolean(meta.s3_url) && meta.s3_validated === true;
        }

        if (datasetData.sample_type === "random") {
          return (
            datasetData.sample_percentage !== undefined &&
            datasetData.sample_percentage > 0 &&
            datasetData.sample_percentage <= 100
          );
        }
        return true;
      case 2:
        return datasetData.name && datasetData.name.trim().length > 0 && 
               datasetData.description && datasetData.description.trim().length > 0;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const canProceed = () => canProceedFromStep(currentStep);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createDataset = async () => {
    setLoading(true);

    const formData = new FormData();
    const meta: any = datasetData.meta || {};
    
    // Add basic fields
    if (datasetData.name) {
      formData.append("name", datasetData.name);
    }
    if (datasetData.description) {
      formData.append("description", datasetData.description);
    }
    formData.append("dataset_type", datasetData.dataset_type || "TRAIN");
    formData.append("sample_type", datasetData.sample_type || "all");
    formData.append("sample_percentage", (datasetData.sample_percentage || 100).toString());
    
    // Add datasource_type - determine if manual or production
    const datasourceType =
      meta.datasource_type || meta.data_source_type || "production";
    formData.append("datasource_type", datasourceType);
    
    // Add file if it's manual datasource
    if (datasourceType === "manual" && meta.uploaded_file) {
      formData.append("file", meta.uploaded_file as File);
    }

    // Add S3 config if it's S3 datasource
    if (datasourceType === "s3") {
      if (meta.s3_url) {
        formData.append("s3_url", meta.s3_url);
      }
      if (meta.s3_region) {
        formData.append("s3_region", meta.s3_region);
      }
      if (meta.s3_file_glob) {
        formData.append("s3_file_glob", meta.s3_file_glob);
      }
    }

    // Add ignored_application_ids as JSON string if present
    if (datasetData.ignored_application_ids && datasetData.ignored_application_ids.length > 0) {
      formData.append("ignored_application_ids", JSON.stringify(datasetData.ignored_application_ids));
    }

    // Add date range if specified
    if (datasourceType === "production" && datasetData.date_start) {
      formData.append("date_start", datasetData.date_start.toString());
    }

    if (datasourceType === "production" && datasetData.date_end) {
      formData.append("date_end", datasetData.date_end.toString());
    }

    if (
      datasetData.base_type === "intent_group" &&
      datasetData.intent_group_id
    ) {
      formData.append("intent_group_id", datasetData.intent_group_id.toString());
    } else if (
      datasetData.base_type === "application_intent" &&
      datasetData.application_id &&
      datasetData.intent_id
    ) {
      formData.append("application_id", datasetData.application_id.toString());
      formData.append("intent_id", datasetData.intent_id.toString());
    }

    DatasetService.createDataset(formData, setLoading, apiKey).subscribe(
      (response: any) => {
        notification.success({
          message: "Dataset created successfully",
          description: "Your dataset has been created successfully",
        });
        localStorage.removeItem(NEW_DATASET_STORAGE_KEY);
        navigate(`/forge/datasets/${response.id}`);
      },
      (error: any) => {
        console.error("Error creating dataset:", error);
        notification.error({
          message: "Error creating dataset",
          description: error.message || "An error occurred while creating the dataset",
        });
        setLoading(false);
      }
    );
  };

  const completedSteps = steps.slice(0, currentStep).filter((_, index) => {
    // Check if each previous step would be valid without causing re-renders
    return canProceedFromStep(index);
  }).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/forge/datasets")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Datasets
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">New Dataset</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                New Dataset
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Create a new training dataset from production data or manual upload
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Progress Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Progress Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-gc-med text-foreground">
                    Step {currentStep + 1} of {steps.length}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {steps[currentStep].title}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-gc-med text-foreground">
                    {completedSteps}/{steps.length} complete
                  </div>
                  <div className="flex items-center gap-1">
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "w-2 h-2 rounded-full transition-colors",
                          index < currentStep ? "bg-maitai-green" : 
                          index === currentStep ? "bg-maitai-green/60" : "bg-muted"
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Step Navigation */}
              <div className="flex space-x-8">
                {steps.map((step, index) => (
                  <div
                    key={step.key}
                    className={cn(
                      "flex items-center gap-3 text-sm transition-colors",
                      index === currentStep
                        ? "text-maitai-green"
                        : index < currentStep
                        ? "text-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full text-xs font-gc-med transition-colors",
                      index === currentStep
                        ? "bg-maitai-green text-white"
                        : index < currentStep
                        ? "bg-maitai-green text-white"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {index < currentStep ? (
                        <CheckIcon className="w-3 h-3" />
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </div>
                    <span className="font-gc-reg">{step.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step Content Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {steps[currentStep].component}
          </CardContent>
        </Card>

        {/* Navigation Card */}
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    disabled={loading}
                    className="gap-2"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Previous Step
                  </Button>
                )}
              </div>
              <div>
                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading}
                    className="gap-2"
                  >
                    Continue
                    <ChevronRightIcon className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    disabled={!canProceed() || loading}
                    onClick={createDataset}
                    className="gap-2"
                  >
                    {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                    {loading ? "Creating dataset..." : (
                      <>
                        <FileTextIcon className="w-4 h-4" />
                        Create Dataset
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DatasetNew;
